import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

export type CourseSource = 'manual' | 'ai';
export type CourseStatus = 'draft' | 'published';

export interface Lesson { id: string; title: string; body: string; position: number }
export interface CourseQuestion { id: string; prompt: string; choices: string[]; correctIndex?: number }

export interface CourseDetail {
  id: string; title: string; description: string | null; source: CourseSource; status: CourseStatus; passPct: number; createdAt: string;
  lessons: Lesson[]; questions: CourseQuestion[];
}
export interface CourseListItem {
  id: string; title: string; description: string | null; source: CourseSource; status: CourseStatus; passPct: number; createdAt: string;
  assigned: number; completed: number;
}
export interface MyCourse {
  id: string; title: string; description: string | null; source: CourseSource; status: CourseStatus; passPct: number; createdAt: string;
  assignedAt: string; completedAt: string | null; scorePct: number | null; passed: boolean | null;
}

export function useCourses() {
  return useQuery({ queryKey: ['learning', 'courses'], queryFn: async () => (await api.get<{ courses: CourseListItem[] }>('/api/learning/courses')).data.courses });
}
export function useMyCourses() {
  return useQuery({ queryKey: ['learning', 'mine'], queryFn: async () => (await api.get<{ courses: MyCourse[] }>('/api/learning/my-courses')).data.courses });
}
export function useCourse(id: string | null) {
  return useQuery({ queryKey: ['learning', 'course', id], queryFn: async () => (await api.get<CourseDetail>(`/api/learning/courses/${id}`)).data, enabled: !!id });
}

function inv(qc: ReturnType<typeof useQueryClient>) { qc.invalidateQueries({ queryKey: ['learning'] }); }

export function useCreateCourse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { title: string; description?: string; passPct?: number; lessons: Array<{ title: string; body: string }>; questions?: Array<{ prompt: string; choices: string[]; correctIndex: number }> }) =>
      (await api.post<CourseDetail>('/api/learning/courses', input)).data,
    onSuccess: () => inv(qc),
  });
}
export function useGenerateCourse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { topic: string; sourceText?: string; passPct?: number }) =>
      (await api.post<CourseDetail>('/api/learning/courses/generate', input)).data,
    onSuccess: () => inv(qc),
  });
}
export function usePublishCourse() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: async (id: string) => (await api.post(`/api/learning/courses/${id}/publish`, {})).data, onSuccess: () => inv(qc) });
}
export function useAssignCourse() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: async (id: string) => (await api.post<{ assigned: number; newlyAssigned: number }>(`/api/learning/courses/${id}/assign`, {})).data, onSuccess: () => inv(qc) });
}
export function useCompleteCourse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, answers }: { id: string; answers: Array<{ questionId: string; choiceIndex: number }> }) =>
      (await api.post<{ scorePct: number; passed: boolean; passPct: number }>(`/api/learning/courses/${id}/complete`, { answers })).data,
    onSuccess: () => inv(qc),
  });
}
